import { getGasAnalysisSpotChart } from "$/api/market-monitoring/gas-analysis.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import LineGraph from "$/lib/components/graphs/LineGraph";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import GraphDateRangeSelector from "$/lib/components/molecules/GraphDateRangeSelector";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import {
  getDate,
  getLast6MonthsStartAndEndDates,
} from "$/lib/utils/functions/date.functions";
import {
  DEFAULT_GRAPH_COLORS,
  handleOnChangeDefaultGraphsTabs,
} from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import { cn, getArrayKeys } from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

const GasSpotPEGDayAheadChart = ({
  id,
  isDraggable = false,
  isShowcase = false,
}: CustomDashboardProps) => {
  const [selectedGraphTabId, setSelectedGraphTabId] = useState("LAST_6_MONTHS");

  const { start, end } = getLast6MonthsStartAndEndDates();

  const [dateRange, setDateRange] = useState<[Date, Date]>([start, end]);

  const [indices, setIndices] = useState<
    | {
        start: number;
        end: number;
      }
    | undefined
  >();

  const { data, isLoading } = useQuery({
    queryKey: ["get-gas-analysis-spot-peg-chart", dateRange],
    queryFn: () =>
      getGasAnalysisSpotChart({
        date: getDate(dateRange[0]),
        endDate: getDate(dateRange[1]),
      }),
  });

  const toDisplay = useMemo(() => (data ? data.spotData : []), [data]);
  const toDisplayVolatility = useMemo(
    () => (data ? data.spotVolatilityData : []),
    [data],
  );

  const keys = useMemo(() => getArrayKeys(toDisplay), [toDisplay]);

  const handleOnChangeDateRange = (values: [Date, Date]) => {
    setDateRange(values);
  };

  const colors: Record<string, string> = keys.reduce(
    (acc, key, i) => ({
      ...acc,
      [key]: key === "PEG Day Ahead" ? "#82b66f" : DEFAULT_GRAPH_COLORS[i],
    }),
    {},
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  useEffect(() => {
    handleOnChangeDefaultGraphsTabs(
      selectedGraphTabId,
      handleOnChangeDateRange,
    );
  }, [selectedGraphTabId]);

  useEffect(() => {
    setGraphVisibility(
      keys.map((key, i) => ({
        name: key,
        color: key === "PEG Day Ahead" ? "#82b66f" : DEFAULT_GRAPH_COLORS[i],
      })),
    );
  }, [keys]);

  useEffect(() => {
    setIndices(undefined);
  }, [dateRange]);

  return (
    <DisplayBox
      title="Prix et volatilité journalière Spot Day Ahead PEG"
      Icon={LineChartIcon}
      theme="green"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      className="rounded-xl shadow-displayBoxShadow"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer>
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <Flexbox fullWidth fullHeight className="gap-2 rounded-3xl">
            <Flexbox fullWidth className="h-[65%]">
              <LineGraph
                data={toDisplay}
                graphVisibility={graphVisibility}
                title="Prix Stlt EEX"
                xAxisDataKey="date"
                syncId="id-gas-analysis-spot-peg"
                colors={colors}
                theme="green"
                keys={keys}
                heightPercent={70}
                leftMargin={-10}
                indices={indices}
              />
            </Flexbox>

            <Flexbox fullWidth className="h-[35%]">
              <LineGraph
                data={toDisplayVolatility}
                title="Volatilité"
                graphVisibility={graphVisibility}
                syncId="id-gas-analysis-spot-peg"
                withBrush
                xAxisDataKey="date"
                colors={colors}
                theme="green"
                keys={keys}
                heightPercent={38}
                yAxisTicksNumber={7}
                leftMargin={-10}
                indices={indices}
                setIndices={setIndices}
              />
            </Flexbox>
          </Flexbox>
        )}

        <FiltersDisplayBox>
          <GraphDateRangeSelector
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedGraphTabId={selectedGraphTabId}
            setSelectedGraphTabId={setSelectedGraphTabId}
            theme="green"
          />

          <GraphFilter
            title="Légende"
            data={graphVisibility}
            handleOnChange={handleOnChangeGraphVisiblity}
          />
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default GasSpotPEGDayAheadChart;
